import { PSD2_TYPE } from "~/utils/const";
import { UNRESUMABLE_REQUISITION_STATUSES } from "~/utils/psd2/constants";

/**
 * The two account aggregators we integrate with differ in almost every detail:
 * the endpoint they are reached through, the shape of the start request, the id
 * used to name a bank, and whether a half-finished sync can be resumed or
 * cancelled at all. Those differences used to be spelled out as
 * `if (psd2Type === GO_CARDLESS)` at every call site, which meant adding an
 * aggregator meant finding every one of them.
 *
 * They are two entries in a table now. What each one can do is data, so there
 * is nothing to inherit and nothing to override: a reader answers "can this one
 * be cancelled" by looking at one line rather than by resolving a class
 * hierarchy three deep.
 */

/**
 * How the business endpoints name the subject of a sync: an application or a
 * prescore by its own id, a standalone company limit by its company.
 */
export function businessSubjectId({ publicId, prescoreId, companyId }) {
    if (publicId || prescoreId) {
        return publicId || prescoreId;
    }
    if (companyId) {
        return `company-${companyId}`;
    }
    throw new Error("PSD2:: a business sync needs a publicId, a prescoreId or a companyId");
}

/**
 * All four start requests, written out.
 *
 * The consumer and business shapes are deliberately not merged. The two
 * segments post to endpoints that share a DTO, and the business service
 * persists whatever `source` and `fieldId` it is handed - so a business sync
 * given the consumer's source would be filed as one. That the business shapes
 * carry neither is the thing to preserve, and it is only visible if they sit
 * next to each other.
 */
const AGGREGATORS = Object.freeze({
    [PSD2_TYPE.A8]: Object.freeze({
        /** The flow config carries the expiry window applied to an earlier sync. */
        needsFlowId: true,
        supportsCancel: true,
        /**
         * A8 closes a sync by its own id, and that id is only ever handed out
         * by the start call - the status endpoint does not carry it. So
         * cancelling is possible exactly while we still hold the note that
         * start left behind, and the flow asks before offering the button.
         */
        cancelNeedsSyncId: true,
        cancel: (store, { syncId }) => store.failSync(syncId),
        /** GoCardless refuses further syncs after a daily cap; A8 does not. */
        hasDailyLimit: false,
        canSync: (bank) => !!bank.aggreg8Id,
        /** A8 never hands back a link to an unfinished bank flow. */
        resumeUrl: () => null,
        consumerStartRequest: ({ source, publicId, correctionId, fieldId, redirectUrl, bank, countryId, addBank }) => ({
            source: source,
            bnplApplicationPublicId: publicId,
            correctionId: correctionId,
            fieldId: fieldId,
            redirectUrl: redirectUrl,
            addBank: addBank,
            bankId: bank.aggreg8Id,
            bankCode: bank.code,
            countryId: countryId ?? null,
        }),
        businessStartRequest: ({ subject, companyId, redirectUrl, bank, countryId, addBank }) => ({
            bnplApplicationPublicId: subject,
            companyId: companyId,
            redirectUrl: redirectUrl,
            addBank: addBank,
            bankId: bank.aggreg8Id,
            bankCode: bank.code,
            countryId: countryId,
        }),
    }),
    [PSD2_TYPE.GO_CARDLESS]: Object.freeze({
        needsFlowId: false,
        supportsCancel: true,
        /** GoCardless cancels whatever is running for the subject. */
        cancelNeedsSyncId: false,
        cancel: (store, { subject }) => store.cancelSync(subject),
        hasDailyLimit: true,
        canSync: (bank) => !!bank.goCardlessId,
        resumeUrl: (latest) => {
            if (!latest || UNRESUMABLE_REQUISITION_STATUSES.includes(latest.requisitionsStatus)) {
                return null;
            }
            return latest.link ?? null;
        },
        consumerStartRequest: ({ source, publicId, correctionId, fieldId, redirectUrl, bank, countryId }) => ({
            source: source,
            publicId: publicId,
            correctionId: correctionId,
            fieldId: fieldId,
            redirectUrl: redirectUrl,
            institutionId: bank.goCardlessId,
            countryId: countryId,
        }),
        businessStartRequest: ({ subject, companyId, redirectUrl, bank, countryId }) => ({
            publicId: subject,
            companyId: companyId,
            redirectUrl: redirectUrl,
            institutionId: bank.goCardlessId,
            countryId: countryId,
        }),
    }),
});

/**
 * The flow's one way of talking to an aggregator.
 *
 * Getters rather than values, because the flow wraps this in a `computed` and
 * reads through it: a snapshot of `latest` taken when the aggregator was chosen
 * would never change again.
 *
 * @param psd2Type   the country's configured aggregator; anything that is not
 *                   GoCardless means A8, matching long-standing behaviour
 * @param store      the sync store for this aggregator and segment
 * @param isBusiness whether the subject of the sync is a company
 */
export function createPsd2Aggregator(psd2Type, { store, isBusiness = false }) {
    const spec = AGGREGATORS[psd2Type] ?? AGGREGATORS[PSD2_TYPE.A8];

    /** Consumer endpoints take the subject from the session, so they get none. */
    const subjectOf = (identity) => isBusiness ? businessSubjectId(identity) : undefined;

    return {
        get latest() {
            return store.latest;
        },
        get dailySyncLimitReached() {
            return spec.hasDailyLimit && store.dailySyncLimitReached;
        },
        /** Can the customer be sent back into an unfinished bank flow? */
        get canResume() {
            return !!spec.resumeUrl(store.latest);
        },
        get resumeUrl() {
            return spec.resumeUrl(store.latest);
        },

        needsFlowId: spec.needsFlowId,
        supportsCancel: spec.supportsCancel,
        cancelNeedsSyncId: spec.cancelNeedsSyncId,

        /**
         * Whether this aggregator can reach a bank at all. The bank list is one
         * list for both of them, with an id per aggregator and neither
         * guaranteed.
         */
        canSync: spec.canSync,

        /** Translates the flow's intent into this aggregator's start request. */
        buildStartRequest(intent) {
            return isBusiness
                ? spec.businessStartRequest({ ...intent, subject: businessSubjectId(intent) })
                : spec.consumerStartRequest(intent);
        },

        /**
         * @param force   "yes" makes the backend re-read the aggregator rather
         *                than answering from its own record
         * @param context the flow id and who the sync is for
         */
        async fetchStatus(force, context = {}) {
            return await store.getStatus({
                force: force,
                // Only the aggregator that uses one is given one, because
                // resolving it can cost the host a request.
                flowId: spec.needsFlowId ? context.flowId : undefined,
                subject: subjectOf(context),
            });
        },

        async start(request) {
            return await store.start(request);
        },

        /** @param context who the sync is for, and the id start handed back */
        async cancel(context = {}) {
            if (spec.cancelNeedsSyncId && !context.syncId) {
                throw new Error(`PSD2:: ${psd2Type} cannot cancel without the id the start call returned`);
            }
            return await spec.cancel(store, {
                subject: subjectOf(context),
                syncId: context.syncId,
            });
        },

        /**
         * The customer telling us the figures we read are wrong. All four
         * stores spell this the same way; only the URL differs, and the store
         * already holds that.
         */
        async mistakeFound(request) {
            return await store.mistakeFound(request);
        },
    };
}
